import React from 'react';
import './Skeleton.scss';

/**
 * Guide §15 — "a skeleton shaped like the content, reserving its final height."
 * Token-driven shimmer (1.6s). Use ONLY for data loading (lists, tables, card
 * grids, dashboards) where the shape is known and the wait is ~300ms–a few
 * seconds. For async button actions keep a spinner (Law 19); for unknown-shape
 * waits use a labelled spinner instead.
 */

/**
 * Keeps a skeleton visible through the brief initial-mount window so a page never
 * flashes an empty state before its fetch sets `loading`. Returns true while
 * loading, or during the first ~200ms when there is still no data.
 */
export const useSkeletonVisible = (loading, isEmpty) => {
	// Stay in skeleton while loading, or — before any load has completed — while
	// there is still no data. We DON'T expire on a short timer (a slow fetch would
	// flash the empty state); we wait for a real load cycle, with a long safety net.
	const [seenLoading, setSeenLoading] = React.useState(false);
	const [expired, setExpired] = React.useState(false);
	React.useEffect(() => {
		if (loading) setSeenLoading(true);
	}, [loading]);
	React.useEffect(() => {
		const t = setTimeout(() => setExpired(true), 12000);
		return () => clearTimeout(t);
	}, []);
	if (loading) return true;
	if (!isEmpty) return false;
	return !(seenLoading || expired);
};

export const Skeleton = ({ width = '100%', height = 14, radius = 4, className = '', style = {} }) => (
	<span
		className={`ivs-sk__bar ${className}`.trim()}
		style={{ width, height, borderRadius: radius, ...style }}
		aria-hidden="true"
	/>
);

export const SkeletonText = ({ lines = 3, width, gap = 10, lastWidth = '60%', className = '' }) => (
	<span className={`ivs-sk ${className}`.trim()} style={{ gap }} aria-hidden="true">
		{Array.from({ length: lines }).map((_, i) => (
			<Skeleton key={i} width={i === lines - 1 ? width || lastWidth : width || '100%'} />
		))}
	</span>
);

export const SkeletonTable = ({ rows = 8, cols = 5, header = true, className = '' }) => {
	const grid = { gridTemplateColumns: `repeat(${cols}, 1fr)` };
	return (
		<div className={`ivs-sk-table ${className}`.trim()} role="status" aria-busy="true" aria-label="Loading">
			{header && (
				<div className="ivs-sk-table__row ivs-sk-table__head" style={grid}>
					{Array.from({ length: cols }).map((_, c) => (
						<Skeleton key={c} height={11} width="65%" />
					))}
				</div>
			)}
			{Array.from({ length: rows }).map((_, r) => (
				<div className="ivs-sk-table__row" key={r} style={grid}>
					{Array.from({ length: cols }).map((_, c) => (
						<Skeleton key={c} height={12} width={c === 0 ? '85%' : c === cols - 1 ? '45%' : '65%'} />
					))}
				</div>
			))}
		</div>
	);
};

export const SkeletonCards = ({ count = 6, media = 150, min = 240, className = '' }) => (
	<div
		className={`ivs-sk-cards ${className}`.trim()}
		style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))` }}
		role="status"
		aria-busy="true"
		aria-label="Loading"
	>
		{Array.from({ length: count }).map((_, i) => (
			<div className="ivs-sk-cards__card" key={i}>
				<Skeleton height={media} radius={10} />
				<div className="ivs-sk-cards__meta">
					<Skeleton height={13} width="70%" />
					<Skeleton height={11} width="45%" />
				</div>
			</div>
		))}
	</div>
);

export const SkeletonKpis = ({ count = 4, min = 190, className = '' }) => (
	<div
		className={`ivs-sk-kpis ${className}`.trim()}
		style={{ gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))` }}
		role="status"
		aria-busy="true"
		aria-label="Loading"
	>
		{Array.from({ length: count }).map((_, i) => (
			<div className="ivs-sk-kpis__kpi" key={i}>
				<Skeleton width="55%" height={11} />
				<Skeleton width="42%" height={26} />
				<Skeleton width="65%" height={10} />
			</div>
		))}
	</div>
);

export const SkeletonChart = ({ height = 280, bars = 12, className = '' }) => (
	<div className={`ivs-sk-chart ${className}`.trim()} style={{ height }} role="status" aria-busy="true" aria-label="Loading chart">
		{Array.from({ length: bars }).map((_, i) => (
			<span
				className="ivs-sk__bar ivs-sk-chart__bar"
				key={i}
				style={{ height: `${25 + ((i * 37) % 70)}%` }}
				aria-hidden="true"
			/>
		))}
	</div>
);

export default Skeleton;
